import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Sidebar from '../components/layout/Sidebar';
import { useAuth } from '../hooks';
import '../styles/Authorities.css';

const NORDIC_BALTIC_NCAS = [
  // Main Countries (EU Member States)
  {
    id: "fi-fsa",
    code: "FI",
    fullName: "Finnish Financial Supervisory Authority",
    localName: "Finanssivalvonta",
    type: "FINANCIAL SUPERVISOR",
    category: "Nordic",
    country: "Finland",
    established: "2009",
    headquarters: "Helsinki",
    description: "Supervises Finnish banks, insurance companies, investment firms, and other financial institutions. Acts as MiCA competent authority and oversees crypto-asset service providers in Finland.",
    responsibilities: ["Banking supervision", "Insurance supervision", "Securities markets", "Crypto asset licensing"],
    frameworks: ["MiCA", "MiFID II", "DORA", "GDPR"],
    website: "https://www.finanssivalvonta.fi",
    mica: true,
    miFID: true,
  },
  {
    id: "se-fi",
    code: "SE",
    fullName: "Swedish Financial Supervisory Authority",
    localName: "Finansinspektionen",
    type: "FINANCIAL SUPERVISOR",
    category: "Nordic",
    country: "Sweden",
    established: "1991",
    headquarters: "Stockholm",
    description: "Swedish financial markets regulator supervising banks, credit market companies, insurance companies, securities companies, and crypto-asset service providers under MiCA framework.",
    responsibilities: ["Banking supervision", "Insurance supervision", "Securities markets", "Crypto asset licensing"],
    frameworks: ["MiCA", "MiFID II", "DORA", "AML"],
    website: "https://www.fi.se",
    mica: true,
    miFID: true,
  },
  {
    id: "dk-dfsa",
    code: "DK",
    fullName: "Danish Financial Supervisory Authority",
    localName: "Finanstilsynet",
    type: "FINANCIAL SUPERVISOR",
    category: "Nordic",
    country: "Denmark",
    established: "1988",
    headquarters: "Copenhagen",
    description: "Supervises all financial undertakings in Denmark including banks, mortgage credit institutions, insurance companies, securities trading, and investment associations. MiCA competent authority for crypto-asset service providers.",
    responsibilities: ["Banking supervision", "Insurance supervision", "Securities markets", "Crypto asset licensing"],
    frameworks: ["MiCA", "MiFID II", "DORA", "AML"],
    website: "https://www.dfsa.dk",
    mica: true,
    miFID: true,
  },
  {
    id: "ee-fsa",
    code: "EE",
    fullName: "Estonian Financial Supervision Authority",
    localName: "Finantsinspektsioon",
    type: "FINANCIAL SUPERVISOR",
    category: "Baltic",
    country: "Estonia",
    established: "2002",
    headquarters: "Tallinn",
    description: "Supervises Estonian financial sector including banks, insurance companies, investment firms, and crypto-asset service providers. Pioneer in digital financial services regulation including MiCA implementation.",
    responsibilities: ["Banking supervision", "Insurance supervision", "Securities markets", "Crypto asset licensing"],
    frameworks: ["MiCA", "MiFID II", "DORA", "AML"],
    website: "https://www.fi.ee",
    mica: true,
    miFID: true,
  },
  {
    id: "lv-fcmc",
    code: "LV",
    fullName: "Financial and Capital Market Commission",
    localName: "Finanšu un kapitāla tirgus komisija",
    type: "FINANCIAL SUPERVISOR",
    category: "Baltic",
    country: "Latvia",
    established: "2001",
    headquarters: "Riga",
    description: "Latvian financial regulator supervising credit institutions, investment firms, insurance companies, and crypto-asset service providers. MiCA competent authority for Latvia.",
    responsibilities: ["Banking supervision", "Insurance supervision", "Securities markets", "Crypto asset licensing"],
    frameworks: ["MiCA", "MiFID II", "DORA", "AML"],
    website: "https://www.fktk.lv",
    mica: true,
    miFID: true,
  },
  {
    id: "lt-lb",
    code: "LT",
    fullName: "Bank of Lithuania",
    localName: "Lietuvos bankas",
    type: "CENTRAL BANK",
    category: "Baltic",
    country: "Lithuania",
    established: "1922",
    headquarters: "Vilnius",
    description: "Lithuania's central bank and financial supervisor. Supervises credit institutions, e-money institutions, payment institutions, and crypto-asset service providers under MiCA framework.",
    responsibilities: ["Monetary policy", "Banking supervision", "Payment systems", "Crypto asset licensing"],
    frameworks: ["MiCA", "MiFID II", "DORA", "AML"],
    website: "https://www.lb.lt",
    mica: true,
    miFID: true,
  },
  // EEA Countries (Non-EU)
  {
    id: "no-fsa",
    code: "NO",
    fullName: "Financial Supervisory Authority of Norway",
    localName: "Finanstilsynet",
    type: "FINANCIAL SUPERVISOR",
    category: "Nordic",
    country: "Norway",
    established: "1986",
    headquarters: "Oslo",
    description: "Norway's financial services regulator supervising banks, insurance companies, securities trading, and auditors. Ensures financial stability and consumer protection in Norwegian financial markets.",
    responsibilities: ["Banking supervision", "Insurance supervision", "Securities markets", "Auditor oversight"],
    frameworks: ["MiFID II", "GDPR", "NIS2"],
    website: "https://www.finanstilsynet.no",
    mica: false,
    miFID: true,
  },
  {
    id: "is-fme",
    code: "IS",
    fullName: "Financial Supervisory Authority of Iceland",
    localName: "Fjármálaeftirlitið",
    type: "FINANCIAL SUPERVISOR",
    category: "Nordic",
    country: "Iceland",
    established: "1999",
    headquarters: "Reykjavik",
    description: "Independent public institution supervising financial activities in Iceland. Oversees banks, insurance companies, securities firms, pension funds, and payment institutions.",
    responsibilities: ["Banking supervision", "Insurance supervision", "Securities markets", "Pension fund oversight"],
    frameworks: ["MiFID II", "GDPR", "NIS2"],
    website: "https://www.fme.is",
    mica: false,
    miFID: true,
  },
];

const CATEGORIES = ["All", "Nordic", "Baltic"];

const NordicBalticNCAs = () => {
  const { currentUser, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredNCAs = NORDIC_BALTIC_NCAS.filter(nca => {
    const matchesCategory = selectedCategory === 'All' || nca.category === selectedCategory;
    const matchesSearch = searchQuery === '' ||
      nca.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      nca.localName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      nca.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
      nca.description.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSelectedCategory('All');
  };

  return (
    <>
      <Header
        currentUser={currentUser}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        onLogout={logout}
      />
      <div className="app">
        <Sidebar />
        <div className="main-content">
          <div className="content-area">

            {/* Hero Section */}
            <div className="authorities-hero">
              <div className="authorities-hero-content">
                <div className="authorities-hero-label">NORDIC-BALTIC NCAS</div>
                <h1 className="authorities-hero-title">Nordic & Baltic National Competent Authorities</h1>
                <p className="authorities-hero-description">
                  Browse {NORDIC_BALTIC_NCAS.length} financial supervisory authorities across Nordic and Baltic states. Main countries: Finland, Sweden, Denmark, Estonia, Latvia, Lithuania. EEA countries: Norway, Iceland.
                </p>
              </div>
            </div>

            {/* Search Section */}
            <div className="authorities-search-section">
              <form onSubmit={handleSearch} className="authorities-search-form">
                <div className="search-input-wrapper">
                  <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input
                    type="text"
                    className="authorities-search-input"
                    placeholder="Search authorities by country, name, or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="clear-button"
                      onClick={() => setSearchQuery('')}
                      title="Clear search"
                    >
                      ×
                    </button>
                  )}
                  <button type="submit" className="search-button">
                    Search
                  </button>
                </div>
              </form>

              {/* Filters */}
              <div className="authorities-filters">
                <div className="filter-group">
                  <label className="filter-label">Region</label>
                  <select
                    className="filter-select"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {CATEGORIES.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-stats">
                  <span className="stats-count">{filteredNCAs.length}</span>
                  <span className="stats-label">authorities found</span>
                </div>
              </div>
            </div>

            {/* NCAs Grid */}
            <div className="authorities-section">
              {filteredNCAs.length === 0 ? (
                <div className="empty-state">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="empty-icon">
                    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                  </svg>
                  <p>No authorities found matching your search criteria.</p>
                  <p className="empty-hint">Try adjusting your filters or search query.</p>
                  <button onClick={handleClearSearch} className="reset-filters-btn">
                    Clear Filters
                  </button>
                </div>
              ) : (
                <div className="authorities-grid">
                  {filteredNCAs.map(nca => (
                    <div key={nca.id} className="authority-card">
                      <div className="authority-header">
                        <div className="authority-type-badge">
                          {nca.type}
                        </div>
                        <div className="authority-code">{nca.code}</div>
                      </div>

                      <h3 className="authority-name">{nca.country} - {nca.fullName}</h3>

                      <p className="authority-description">{nca.description}</p>

                      <div className="authority-meta">
                        <div className="meta-item">
                          <span className="meta-label">Local Name:</span>
                          <span className="meta-value">{nca.localName}</span>
                        </div>
                        <div className="meta-item">
                          <span className="meta-label">Established:</span>
                          <span className="meta-value">{nca.established}</span>
                        </div>
                        <div className="meta-item">
                          <span className="meta-label">Headquarters:</span>
                          <span className="meta-value">{nca.headquarters}</span>
                        </div>
                      </div>

                      <div className="authority-responsibilities">
                        <h4>Key Responsibilities</h4>
                        <ul>
                          {nca.responsibilities.map((resp, idx) => (
                            <li key={idx}>{resp}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="authority-frameworks">
                        <span className="frameworks-label">FRAMEWORKS</span>
                        <div className="frameworks-tags">
                          {nca.frameworks.map(framework => (
                            <span key={framework} className="framework-tag">{framework}</span>
                          ))}
                        </div>
                      </div>

                      <div className="authority-footer">
                        <a
                          href={nca.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="authority-link"
                        >
                          View Website
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default NordicBalticNCAs;
